import React, { useState, useEffect } from 'react';
import { Smartphone, Lock, CheckCircle, AlertCircle, Mail } from 'lucide-react';
import { farmvestService } from '../../services/farmvest_api';
import { mleService } from '../../services/mleService';

interface LoginProps {
  onLogin: (session: { mobile: string; role: string | null }) => void;
}

const SESSION_KEY = 'ak_dashboard_session';

// Decode JWT payload without a library and return expiry epoch (seconds), or 0 if unreadable.
function getJwtExpiry(token: string): number {
  try {
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.exp === 'number' ? decoded.exp : 0;
  } catch {
    return 0;
  }
}

function isTokenExpired(token: string): boolean {
  const exp = getJwtExpiry(token);
  if (!exp) return true; // no expiry claim → treat as expired to be safe
  return Date.now() / 1000 >= exp;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [loginMethod, setLoginMethod] = useState<'mobile' | 'email'>('mobile');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'enterIdentifier' | 'enterOtp'>('enterIdentifier');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [timer, setTimer] = useState(90);
  const [isTimerActive, setIsTimerActive] = useState(false);

  // Admin login (no OTP)
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminMobile, setAdminMobile] = useState('');
  const [adminKey, setAdminKey] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const longPressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLongPressStart = () => {
    longPressTimer.current = setTimeout(() => {
      setShowAdminModal(true);
      setAdminError(null);
      setAdminMobile('');
      setAdminKey('');
    }, 800);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Generate the client RSA key pair as early as possible so it is ready
  // by the time the user submits their OTP. The public key is sent to the
  // server inside loginWithOtp and the returned server_public_key activates MLE.
  useEffect(() => {
    mleService.initialize().catch((err) => {
      console.warn('[MLE] Key generation failed — requests will use Bearer token only:', err);
    });
  }, []);

  useEffect(() => {
    const saved = window.localStorage.getItem(SESSION_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (!parsed?.mobile) return;

        // Reject expired tokens — don't silently restore a stale session
        if (parsed.access_token && isTokenExpired(parsed.access_token)) {
          window.localStorage.removeItem(SESSION_KEY);
          return;
        }

        onLogin(parsed);
      } catch {
        window.localStorage.removeItem(SESSION_KEY);
      }
    }
  }, [onLogin]);

  const sendOtp = async () => {
    if (loginMethod === 'mobile') {
      if (!mobile || mobile.length < 10) {
        setError('Please enter a valid mobile number');
        return;
      }
    } else {
      if (!email || !email.includes('@')) {
        setError('Please enter a valid email address');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setInfo(null);
    setOtp('');

    try {
      if (loginMethod === 'mobile') {
        await farmvestService.sendWhatsappOtp(mobile);
        setInfo('Verification code sent to your WhatsApp.');
      } else {
        await farmvestService.sendEmailOtp(email);
        setInfo('Verification code sent to your email.');
      }
      setStep('enterOtp');
      setTimer(90);
      setIsTimerActive(true);
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.response?.data?.message || 'Failed to send OTP. Please try again.';
      if (typeof msg === 'string' && msg.toLowerCase().includes('not registered')) {
        setError('Mobile number not registered. Please contact your administrator.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    // Consistent 6-digit requirement in both the guard and the form submit
    if (!otp || otp.length !== 6) {
      setError(loginMethod === 'mobile'
        ? 'Please enter the 6-digit OTP received on WhatsApp'
        : 'Please enter the 6-digit OTP received on your email');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = loginMethod === 'mobile'
        ? await farmvestService.loginWithOtp(mobile, otp)
        : await farmvestService.loginWithEmailOtp(email, otp);

      if (data && data.access_token) {

        let role = data.role || (data.user && data.user.role);
        let name = data.name || (data.user && data.user.name) || 'Admin';

        // standard roles (RoleEnum values only) — mobile-visible
        // department_roles — custom department roles e.g. MARKETING, FINANCE (dashboard only)
        const rolesSource = data.roles || (data.user && data.user.roles);
        const deptRoles: Record<string, boolean> = data.department_roles || (data.user && data.user.department_roles) || {};

        // Field-staff roles that must not access the dashboard
        const FIELD_STAFF_ROLES = new Set(['FARM_MANAGER', 'SUPERVISOR', 'DOCTOR', 'ASSISTANT_DOCTOR', 'INVESTOR']);

        if (rolesSource && typeof rolesSource === 'object' && !Array.isArray(rolesSource)) {
          // Object format: { ROLE_NAME: true/false }
          // Priority: SUPERADMIN > ADMIN > FARMVEST_ADMIN (field-staff roles skipped)
          const PRIORITY = ['SUPERADMIN', 'SUPER_ADMIN', 'ADMIN', 'FARMVEST_ADMIN'];
          let activeRole: string | null = null;
          for (const pr of PRIORITY) {
            if (rolesSource[pr] === true) { activeRole = pr; break; }
          }
          // Skip field-staff roles — fall through to department_roles
          if (!activeRole) {
            // Check custom department roles (MARKETING, FINANCE, etc.)
            const deptFound = Object.entries(deptRoles).find(([, v]) => v === true);
            if (deptFound) {
              activeRole = deptFound[0];
            } else {
              // All active roles are field-staff or all roles are inactive
              const hasBlockedActive = Object.entries(rolesSource).some(([k, v]) => v === true && FIELD_STAFF_ROLES.has(k.toUpperCase()));
              if (hasBlockedActive) {
                setError('Dashboard access is not available for this role.');
              } else {
                const hasAnyRole = Object.keys(rolesSource).length > 0 || Object.keys(deptRoles).length > 0;
                setError(hasAnyRole ? 'Your role is inactive. Contact administration.' : 'No role assigned to this account.');
              }
              setLoading(false);
              return;
            }
          }
          if (activeRole === 'SUPER_ADMIN' || activeRole === 'SUPERADMIN') role = 'SUPER ADMIN';
          else if (activeRole === 'ADMIN') role = 'ADMIN';
          else if (activeRole === 'FARMVEST_ADMIN') role = 'FARMVEST ADMIN';
          else role = activeRole; // dynamic department role e.g. 'MARKETING', 'FINANCE'

        } else if (rolesSource && Array.isArray(rolesSource)) {
          // Array format: ["ADMIN", "MARKETING", ...]
          const r = rolesSource.map((x: string) => String(x).toUpperCase());
          if (r.includes('SUPER_ADMIN') || r.includes('SUPERADMIN')) role = 'SUPER ADMIN';
          else if (r.includes('ADMIN')) role = 'ADMIN';
          else if (r.includes('FARMVEST_ADMIN') || r.includes('FARMVEST ADMIN')) role = 'FARMVEST ADMIN';
          // Field-staff roles: skip — check department_roles instead
          if (!role || role === 'User') {
            const deptFound = Object.entries(deptRoles).find(([, v]) => v === true);
            if (deptFound) {
              role = deptFound[0];
            } else if (r.length > 0 && r.every(rv => FIELD_STAFF_ROLES.has(rv))) {
              setError('Dashboard access is not available for this role.');
              setLoading(false);
              return;
            }
          }
        }

        if (!role) {
          setError('No role assigned to this account.');
          setLoading(false);
          return;
        }

        const resolvedMobile = loginMethod === 'email'
          ? (data.mobile || data.user?.mobile || '')
          : mobile;

        let sessionRoles: string[] = [];
        if (rolesSource && typeof rolesSource === 'object' && !Array.isArray(rolesSource)) {
          sessionRoles = Object.entries(rolesSource).filter(([_, v]) => v === true).map(([k]) => k);
        } else if (rolesSource && Array.isArray(rolesSource)) {
          sessionRoles = rolesSource.map((x: any) => String(x));
        }
        if (deptRoles && typeof deptRoles === 'object') {
          const deptRolesList = Object.entries(deptRoles).filter(([_, v]) => v === true).map(([k]) => k);
          sessionRoles = [...sessionRoles, ...deptRolesList];
        }
        if (sessionRoles.length === 0 && role) {
          sessionRoles = [role];
        }

        const session: Record<string, any> = {
          mobile: resolvedMobile,
          role,
          roles: sessionRoles,
          name,
          location_name: data.location_name || (data.user && data.user.location_name),
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          token_type: data.token_type || 'Bearer',
          user_id: data.user?.user_id || data.user_id || null,
          screen_access: data.user?.screen_access || data.screen_access || {},
        };

        const isSuperAdmin = role && (
          role.toLowerCase() === 'super admin' ||
          role.toLowerCase() === 'superadmin' ||
          role.toLowerCase() === 'super_admin'
        );

        if (!isSuperAdmin) {
          try {
            // Write token first so the request interceptor can attach it to getUserData
            window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
            const userData = await farmvestService.getUserData(resolvedMobile);
            const farmLocation = userData?.farm_location || userData?.data?.farm_location || userData?.user?.farm_location;
            if (farmLocation) session.location_name = farmLocation;

            const farmId = userData?.farm_id || userData?.data?.farm_id || userData?.user?.farm_id;
            if (farmId) session.farm_id = String(farmId);

            const userObj = userData?.user || userData?.data?.user || userData?.data;
            if (userObj) {
              let roleFromApi = null;
              if (Array.isArray(userObj.roles_detail) && userObj.roles_detail.length > 0) {
                const nonInvestor = userObj.roles_detail.find((rd: any) => rd && String(rd.role || '').toUpperCase() !== 'INVESTOR');
                roleFromApi = nonInvestor ? nonInvestor.role : userObj.roles_detail[0].role;
              }
              if (roleFromApi) session.role = roleFromApi;

              const resolvedId = userObj.user_id || userObj.id || userObj.employee_id;
              if (resolvedId) session.user_id = resolvedId;

              if (Array.isArray(userObj.roles_detail)) {
                session.roles = userObj.roles_detail.map((rd: any) => rd.role).filter(Boolean);
              }
            }
          } catch {
            // Non-fatal: proceed with initial session data
          }
        }

        // Final write with complete session data (overwrites the interim write above)
        window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
        handleLoginSuccess(session);
      } else {
        setError('Login failed: Invalid response from server');
      }
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.response?.data?.message || 'Login failed. Please check your credentials.';
      if (typeof msg === 'string' && msg.toLowerCase().includes('not registered')) {
        setError('Mobile number not registered. Please contact your administrator.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (session: any) => {
    setShowSnackbar(true);
    setTimeout(() => {
      onLogin(session);
    }, 2000);
  };

  const handleAdminLogin = async () => {
    if (adminMobile.length !== 10 || !adminKey.trim()) {
      setAdminError('Enter a valid 10-digit mobile and admin key');
      return;
    }
    setAdminLoading(true);
    setAdminError(null);
    try {
      const data = await farmvestService.adminLogin(adminMobile, adminKey.trim());
      if (!data?.access_token) throw new Error('Invalid response');

      const rolesSource = data.roles || [];
      const r = (Array.isArray(rolesSource) ? rolesSource : Object.keys(rolesSource))
        .map((x: string) => String(x).toUpperCase());
      let role = 'ADMIN';
      if (r.includes('SUPERADMIN') || r.includes('SUPER_ADMIN')) role = 'SUPER ADMIN';
      else if (r.includes('ADMIN')) role = 'ADMIN';

      const session = {
        mobile: adminMobile,
        role,
        name: data.name || 'Admin',
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        token_type: data.token_type || 'Bearer',
        user_id: data.user_id || null,
        screen_access: data.screen_access || {},
      };

      window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      setShowAdminModal(false);
      handleLoginSuccess(session);
    } catch (e: any) {
      setAdminError(e.response?.data?.detail || 'Invalid mobile or admin key');
    } finally {
      setAdminLoading(false);
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isTimerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsTimerActive(false);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerActive, timer]);

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-white font-sans">
      {/* Success Snackbar */}
      {showSnackbar && (
        <div className="fixed top-10 right-1/2 translate-x-1/2 lg:translate-x-0 lg:right-10 z-[100] animate-in fade-in slide-in-from-top-10 duration-500">
          <div className="bg-green-600 text-white px-6 lg:px-8 py-3 lg:py-4 rounded-2xl shadow-[0_20px_50px_rgba(22,163,74,0.3)] flex items-center gap-3 lg:gap-4 border border-green-500/50 backdrop-blur-sm w-max max-w-[90vw]">
            <div className="bg-white/20 p-1.5 lg:p-2 rounded-full shrink-0">
              <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
            </div>
            <div className="whitespace-nowrap">
              <p className="font-semibold text-base lg:text-lg">Success!</p>
              <p className="text-sm lg:text-base text-white/90">Welcome to Dashboard</p>
            </div>
          </div>
        </div>
      )}

      {/* Left side: Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 py-12 lg:py-24 relative bg-white overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600"></div>
        <div className="absolute -top-24 -left-24 w-64 h-64 bg-indigo-50 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-pulse"></div>
        <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-blue-50 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-pulse" style={{ animationDelay: '2s' }}></div>

        <div className="w-full max-w-md relative z-10">
          <div className="space-y-6">
            <div className="space-y-2 text-center">
              <div className="flex justify-center mb-2">
                <img src="/header-logo.png" alt="FarmVest" className="h-16 lg:h-20 object-contain" />
              </div>
              <h1
                className="text-3xl lg:text-5xl font-black text-gray-900 tracking-tight leading-tight select-none cursor-default"
                onMouseDown={handleLongPressStart}
                onMouseUp={handleLongPressEnd}
                onMouseLeave={handleLongPressEnd}
                onTouchStart={handleLongPressStart}
                onTouchEnd={handleLongPressEnd}
              >
                Welcome back
              </h1>
              <p className="text-gray-500 text-base lg:text-lg font-medium">
                Sign in to manage your farm assets
              </p>
            </div>

            {/* Error / Info */}
            {error && (
              <div className="flex items-center gap-3 text-red-600 text-sm bg-red-50 p-4 rounded-xl border border-red-100 animate-in fade-in zoom-in duration-300">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">{error}</span>
              </div>
            )}
            {info && (
              <div className="flex items-center gap-3 text-green-600 text-sm bg-green-50 p-4 rounded-xl border border-green-100 animate-in fade-in zoom-in duration-300">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">{info}</span>
              </div>
            )}

            {/* Login method tabs */}
            {step === 'enterIdentifier' && (
              <div className="flex rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => { setLoginMethod('mobile'); setError(null); setInfo(null); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-bold transition-colors ${loginMethod === 'mobile' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                >
                  <Smartphone size={14} /> Mobile OTP
                </button>
                <button
                  type="button"
                  onClick={() => { setLoginMethod('email'); setError(null); setInfo(null); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-bold transition-colors ${loginMethod === 'email' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                >
                  <Mail size={14} /> Email OTP
                </button>
              </div>
            )}

            <form
              className="space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                if (step === 'enterIdentifier') sendOtp();
                else if (step === 'enterOtp' && otp.length === 6) verifyOtp();
              }}
            >
              {/* Mobile */}
              {loginMethod === 'mobile' && (
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 ml-1">Mobile Number</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Smartphone className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    </div>
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      disabled={step === 'enterOtp'}
                      className="block w-full pl-12 pr-4 py-3 lg:py-4 border border-gray-200 rounded-2xl bg-gray-50/50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white focus:border-transparent transition-all duration-200 text-lg"
                      placeholder="Enter mobile number"
                      autoFocus={step === 'enterIdentifier' && loginMethod === 'mobile'}
                    />
                  </div>
                </div>
              )}

              {/* Email */}
              {loginMethod === 'email' && (
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 ml-1">Email Address</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={step === 'enterOtp'}
                      className="block w-full pl-12 pr-4 py-3 lg:py-4 border border-gray-200 rounded-2xl bg-gray-50/50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white focus:border-transparent transition-all duration-200 text-lg"
                      placeholder="Enter email address"
                      autoFocus={step === 'enterIdentifier' && loginMethod === 'email'}
                    />
                  </div>
                </div>
              )}

              {/* OTP */}
              {step === 'enterOtp' && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-4 duration-500">
                  <label className="text-sm font-semibold text-gray-700 ml-1">Verification Code</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    </div>
                    <input
                      type="text"
                      autoFocus
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="block w-full pl-12 pr-4 py-3 lg:py-4 border border-gray-200 rounded-2xl bg-gray-50/50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white focus:border-transparent transition-all duration-200 tracking-[0.5em] text-center text-xl font-bold"
                      placeholder="••••••"
                      maxLength={6}
                    />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="pt-2 lg:pt-4 space-y-4">
                {step === 'enterIdentifier' ? (
                  <button
                    type="submit"
                    disabled={loading || (loginMethod === 'mobile' ? mobile.length !== 10 : !email.includes('@'))}
                    className="w-full flex justify-center py-3 lg:py-4 px-6 rounded-2xl text-base font-bold text-white transition-all duration-300 shadow-[0_10px_30px_rgba(79,70,229,0.3)] hover:shadow-[0_20px_40px_rgba(79,70,229,0.4)] hover:-translate-y-0.5 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-widest bg-gradient-to-r from-indigo-600 to-blue-600"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Processing...</span>
                      </div>
                    ) : 'Submit'}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <button
                      type="submit"
                      disabled={loading || otp.length !== 6}
                      className="w-full flex justify-center py-3 lg:py-4 px-6 rounded-2xl text-base font-bold text-white bg-gradient-to-r from-emerald-600 to-green-500 hover:shadow-[0_15px_30px_rgba(16,185,129,0.3)] active:scale-[0.98] transition-all duration-300 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-widest"
                    >
                      {loading ? 'Verifying...' : 'Enter OTP'}
                    </button>

                    {timer > 0 ? (
                      <div className="w-full text-center py-3 px-6 rounded-2xl text-sm font-semibold text-gray-500 bg-gray-50 border border-gray-100/50">
                        Resend OTP in <span className="text-indigo-600 font-bold">{timer}s</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={sendOtp}
                        disabled={loading}
                        className="w-full flex justify-center items-center gap-2 py-3 lg:py-4 px-6 rounded-2xl text-base font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100/80 active:scale-[0.98] transition-all duration-300 border border-indigo-100/50 disabled:opacity-50 tracking-widest uppercase"
                      >
                        {loading ? 'Sending...' : 'Resend OTP'}
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        setStep('enterIdentifier');
                        setOtp('');
                        setError(null);
                        setInfo(null);
                        setTimer(30);
                        setIsTimerActive(false);
                      }}
                      className="w-full py-2 text-sm font-semibold text-gray-400 hover:text-blue-600 transition-colors duration-200"
                    >
                      {loginMethod === 'mobile' ? 'Change Mobile Number' : 'Change Email Address'}
                    </button>
                  </div>
                )}
              </div>
            </form>

            {/* Footer */}
            <div className="pt-6 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 border border-gray-100 mb-2">
                <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                <p className="text-gray-400 text-[10px] font-bold tracking-[0.2em] uppercase">
                  Powered by Markwave AI
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side: Visual */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden bg-gray-100">
        <img
          src="/dashboard_wallpaper.png"
          alt="FarmVest Dashboard"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-10000 hover:scale-110"
        />
        <div className="absolute inset-0 flex flex-col justify-end pb-40 p-12 bg-gradient-to-t from-black/80 via-black/20 to-transparent">
          <div className="max-w-md">
            <h2 className="text-3xl font-bold text-white mb-4 leading-tight">
              Optimize your farm investments efficiently.
            </h2>
            <div className="w-20 h-1.5 rounded-full bg-indigo-500"></div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-in { animation: fadeIn 0.4s ease-out; }
        .fade-in { opacity: 1; }
      `}</style>

      {/* Admin Login Modal */}
      {showAdminModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4 space-y-5">
            <div className="text-center">
              <h2 className="text-xl font-black text-gray-900">Admin Access</h2>
              <p className="text-sm text-gray-400 mt-1">Login without OTP</p>
            </div>

            {adminError && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-xl border border-red-100">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{adminError}</span>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Mobile Number</label>
              <input
                type="tel"
                value={adminMobile}
                onChange={(e) => setAdminMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="block w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
                placeholder="10-digit mobile"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Admin Key</label>
              <input
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
                className="block w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
                placeholder="Enter admin key"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowAdminModal(false)}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAdminLogin}
                disabled={adminLoading}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:opacity-90 disabled:opacity-50 transition-all"
              >
                {adminLoading ? 'Logging in...' : 'Login'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;